import React from 'react';
import Tooltip from '../tooltip/Tooltip';
import { eventRules } from '../../util/eventRules';
import './EventStatus.css';

// Stage-Definition: Reihenfolge der Progression von "Spieltag entstanden" bis "fertig".
// Cancelled ist KEIN regulärer Stage — er ersetzt den letzten Slot (Completed) und färbt
// alle vorherigen Slots ab, weil die Progression abgebrochen wurde.
const STAGES = [
  { key: 'Draft',          label: 'Entwurf',          icon: 'edit_note', colorKey: 'warning'  },
  { key: 'MemberPreview',  label: 'Member-Vorschau',  icon: 'group',     colorKey: 'accent'   },
  { key: 'Published',      label: 'Veröffentlicht',   icon: 'public',    colorKey: 'accent'   },
  { key: 'Completed',      label: 'Abgeschlossen',    icon: 'check',     colorKey: 'positive' },
];

// Nie öffentliche Events ohne Gruppen-Freigabe (z. B. Feldmiete): nur Draft ("In Bearbeitung") und Completed.
const STAGES_NONPUBLIC = [
  { key: 'Draft',      label: 'In Bearbeitung', icon: 'pending', colorKey: 'warning'  },
  { key: 'Completed',  label: 'Abgeschlossen',  icon: 'check',   colorKey: 'positive' },
];

// Nie öffentliche Events mit Link-Anmeldung (Privat) bzw. mit Gruppen-Freigabe: wie oben, plus die
// Freigabe für Gruppen (z. B. Mitglieder). Nie "Veröffentlicht" — sie erscheinen nie auf der Homepage.
const STAGES_PRIVATE = [
  { key: 'Draft',          label: 'In Bearbeitung',          icon: 'pending', colorKey: 'warning'  },
  { key: 'MemberPreview',  label: 'Für Member freigegeben',  icon: 'group',   colorKey: 'accent'   },
  { key: 'Completed',      label: 'Abgeschlossen',           icon: 'check',   colorKey: 'positive' },
];

const CANCELLED_STAGE = { key: 'Cancelled', label: 'Abgesagt', icon: 'close', colorKey: 'negative' };

// Template rules that pick the stage set: from `event` (eventRules) or, without an event, from the
// flags neverPublic/linkSignup (icon legend, Markdown docs). Without both: stages of public events.
function stageRules(event, neverPublic, linkSignup) {
  return event ? eventRules(event) : { neverPublic: !!neverPublic, linkSignup: !!linkSignup };
}

function stagesFor({ neverPublic, linkSignup }, currentKey) {
  if (!neverPublic) return STAGES;
  // Freigabe-Stufe: immer bei Link-Anmeldung (der Prozess kann Gruppen einladen), sonst nur, wenn das
  // Event gerade für eine Gruppe freigegeben ist (manuelle Freigabe im Bearbeiten-Popup).
  return linkSignup || currentKey === 'MemberPreview' ? STAGES_PRIVATE : STAGES_NONPUBLIC;
}

// MemberPreview mit bekannter Freigabe ("Mitglieder") → "Freigegeben für Mitglieder"; sonst das Stage-Label.
function stageLabel(stage, releasedFor) {
  if (stage.key === 'MemberPreview' && releasedFor) return `Freigegeben für ${releasedFor}`;
  return stage.label;
}

// Backend liefert State als String (JsonStringEnumConverter ist aktiv) — das ist der
// Standardpfad. Numerische IDs kommen nur aus der IconLegende und Markdown-Docs
// ({{eventstatus:1}}) und folgen dem Legacy-Mapping aus der alten Pillen-Komponente:
//   1 → Draft, 2 → Published, 3 → Completed, 4 → Cancelled, 5 → MemberPreview.
function normalizeState(value) {
  if (typeof value === 'string') return value;
  switch (value) {
    case 1: return 'Draft';
    case 2: return 'Published';
    case 3: return 'Completed';
    case 4: return 'Cancelled';
    case 5: return 'MemberPreview';
    default: return null;
  }
}

// Klartext-Label des Status passend zum Event (z. B. für Info-Popups neben dem Status-Knoten).
// options.event: event with template rules (or the flags neverPublic/linkSignup without an event);
// options.releasedFor: freigegebene Gruppen als Text ("Mitglieder", siehe releaseSummary) für MemberPreview.
export function eventStatusLabel(statusId, { event = null, neverPublic = false, linkSignup = false, releasedFor = '' } = {}) {
  const key = normalizeState(statusId);
  if (key === CANCELLED_STAGE.key) return CANCELLED_STAGE.label;
  const stage = stagesFor(stageRules(event, neverPublic, linkSignup), key).find(s => s.key === key);
  return stage ? stageLabel(stage, releasedFor) : '';
}

// event (optional): event object — the stages follow its template rules (eventRules).
// neverPublic / linkSignup (optional, without event): the rule flags directly (icon legend, docs tokens).
// releasedFor (optional): "Mitglieder" → Tooltip "Freigegeben für Mitglieder" statt "Member-Vorschau".
function EventStatus({
  statusId = 0, style = {}, compact = false, single = false,
  event = null, neverPublic = false, linkSignup = false, releasedFor = '',
}) {
  const currentKey = normalizeState(statusId);
  const isCancelled = currentKey === CANCELLED_STAGE.key;
  const stages = stagesFor(stageRules(event, neverPublic, linkSignup), currentKey);
  const activeIndex = isCancelled
    ? stages.length - 1 // Cancelled belegt den letzten Slot, wird aber visuell als Abbruch dargestellt
    : stages.findIndex(s => s.key === currentKey);

  // Label aus den typ-spezifischen Stages (vorher fälschlich immer aus STAGES → falscher Tooltip
  // bei Feldmiete/Privat, z. B. "Member-Vorschau" statt "Abgeschlossen").
  const tooltipLabel = isCancelled
    ? CANCELLED_STAGE.label
    : (activeIndex >= 0 ? stageLabel(stages[activeIndex], releasedFor) : '');

  if (single) {
    const stageForSingle = isCancelled ? CANCELLED_STAGE : (activeIndex >= 0 ? stages[activeIndex] : null);
    if (!stageForSingle) return null;
    const nodeClass = isCancelled
      ? 'eventStatusNode isCancelled'
      : `eventStatusNode isActive isActive--${stageForSingle.colorKey}`;
    return (
      <Tooltip text={tooltipLabel}>
        <div className={`eventStatusTrack ${compact ? 'compact' : ''}`} style={style}>
          <span className={nodeClass} role="img" aria-label={tooltipLabel}>
            <span className="material-symbols-rounded eventStatusIcon">{stageForSingle.icon}</span>
          </span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={tooltipLabel}>
      <div
        className={`eventStatusTrack ${compact ? 'compact' : ''} ${isCancelled ? 'cancelled' : ''}`}
        role="img"
        aria-label={tooltipLabel}
        style={style}
      >
        {stages.map((stage, idx) => {
          const isLast = idx === stages.length - 1;
          const isActive = !isCancelled && idx === activeIndex;
          const isReached = !isCancelled && idx < activeIndex;
          // Letzter Slot bei Cancelled = Cancelled-Marker; alle anderen "lebten" mal, sind aber abgebrochen.
          const renderedStage = (isCancelled && isLast) ? CANCELLED_STAGE : stage;

          // Klasse für jeden Knoten: bestimmt Farbe (filled vs outline) und ob das "aktiv jetzt"-Highlight an ist.
          let nodeClass = 'eventStatusNode';
          if (isCancelled) {
            nodeClass += isLast ? ' isCancelled' : ' isFaded';
          } else if (isActive) {
            nodeClass += ` isActive isActive--${stage.colorKey}`;
          } else if (isReached) {
            nodeClass += ' isReached';
          } else {
            nodeClass += ' isPending';
          }

          // Klasse für die Linie RECHTS vom Knoten — entscheidet, ob die Strecke schon "gelaufen" ist.
          // Bei Cancelled wird die letzte Linie rot, vorherige bleiben gedämpft.
          let connectorClass = 'eventStatusConnector';
          if (!isLast) {
            if (isCancelled) {
              connectorClass += ' isFaded';
            } else if (idx < activeIndex) {
              connectorClass += ' isReached';
            }
          }

          return (
            <React.Fragment key={renderedStage.key + idx}>
              <span className={nodeClass}>
                <span className="material-symbols-rounded eventStatusIcon">
                  {renderedStage.icon}
                </span>
              </span>
              {!isLast && <span className={connectorClass} />}
            </React.Fragment>
          );
        })}
      </div>
    </Tooltip>
  );
}

export default EventStatus;
